import { useTranslation } from 'react-i18next';
import { phraseRef } from '@il-colpo/engine';
import { Button } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { Bystander, ClueLine, HeardList, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';

interface Cell {
  x: number;
  y: number;
}
type Mirror = '/' | '\\';
interface LaserView {
  size: number;
  room?: {
    emitter: Cell;
    receiver: Cell;
    caseCell: Cell;
    obstacles: Cell[];
    mirrors: (Cell & { orient: Mirror })[];
  };
  lastFireMs?: number;
  preview?: {
    emitter: Cell;
    receiver: Cell;
    caseCell: Cell;
    obstacles: Cell[];
    mirrorPositions: Cell[];
    beam: Cell[];
    end: string;
  };
}

const CELL = 40;
const RECHARGE_MS = 3000;
const HEARD = ['laserMirror', 'laserEnds'] as const;

/** Laser (GDD §8.5): the locksmith turns the mirrors, the hacker watches where the beam goes. */
export function LaserPanel({ puzzleId, type, v: raw }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { role, act, view, g } = useGame();
  const v = raw as unknown as LaserView;
  const size = v.size * CELL;

  if (role === 'locksmith' && v.room) {
    const room = v.room;
    const ready = (view.exec?.elapsedMs ?? 0) - (v.lastFireMs ?? -RECHARGE_MS) >= RECHARGE_MS;
    return (
      <div className={styles['stack']}>
        <p className={styles['muted']}>{t('puzzle.laser.hint')}</p>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className={styles['board']}
          role="group"
          aria-label={t('puzzle.laser.board')}
        >
          <Floor size={v.size} />
          <Hardware room={room} />
          {room.mirrors.map((m, i) => (
            <g
              key={i}
              role="button"
              tabIndex={0}
              aria-label={t('puzzle.laser.mirror', { n: i + 1, orient: m.orient })}
              className={styles['mirror']}
              transform={`translate(${m.x * CELL},${m.y * CELL})`}
              onClick={() => act(puzzleId, { kind: 'laser.flip', mirror: i })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  act(puzzleId, { kind: 'laser.flip', mirror: i });
                }
              }}
            >
              <rect width={CELL} height={CELL} rx={6} />
              <line
                x1={m.orient === '/' ? 8 : 8}
                y1={m.orient === '/' ? CELL - 8 : 8}
                x2={CELL - 8}
                y2={m.orient === '/' ? 8 : CELL - 8}
              />
              <text x={CELL - 6} y={12} textAnchor="end">
                {i + 1}
              </text>
            </g>
          ))}
        </svg>
        <Button disabled={!ready} icon="bolt" onClick={() => act(puzzleId, { kind: 'laser.fire' })}>
          {ready ? t('puzzle.laser.fire') : t('puzzle.laser.recharging')}
        </Button>
        <HeardList puzzleId={puzzleId} ids={HEARD} />
      </div>
    );
  }

  if (role === 'hacker' && v.preview) {
    const p = v.preview;
    const last = p.beam[p.beam.length - 1];
    return (
      <div className={styles['stack']}>
        <p className={styles['muted']}>{t('puzzle.laser.previewHint')}</p>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className={styles['board']}
          role="img"
          aria-label={t('puzzle.laser.preview')}
        >
          <Floor size={v.size} />
          <Hardware
            room={{
              ...p,
              mirrors: p.mirrorPositions.map((m) => ({ ...m, orient: '/' })),
            }}
            hideMirrorGlyph
          />
          <polyline
            className={styles['beam']}
            points={[p.emitter, ...p.beam]
              .map((c) => `${c.x * CELL + CELL / 2},${c.y * CELL + CELL / 2}`)
              .join(' ')}
          />
        </svg>
        <p className={styles['status']}>{g(`puzzle.laser.end.${p.end}`)}</p>
        {last ? (
          <ul className={styles['clues']}>
            <ClueLine phrase={phraseRef('laserEnds', last.x, last.y)} />
          </ul>
        ) : null}
        <HeardList puzzleId={puzzleId} ids={HEARD} />
      </div>
    );
  }
  return <Bystander type={type} />;
}

function Floor({ size }: { size: number }) {
  return (
    <>
      {Array.from({ length: size * size }, (_, i) => (
        <rect
          key={i}
          className={styles['cell']}
          x={(i % size) * CELL}
          y={Math.floor(i / size) * CELL}
          width={CELL}
          height={CELL}
        />
      ))}
    </>
  );
}

function Hardware({
  room,
  hideMirrorGlyph = false,
}: {
  room: {
    emitter: Cell;
    receiver: Cell;
    caseCell: Cell;
    obstacles: Cell[];
    mirrors: (Cell & { orient: Mirror })[];
  };
  hideMirrorGlyph?: boolean;
}) {
  const at = (c: Cell) => `translate(${c.x * CELL},${c.y * CELL})`;
  return (
    <>
      {room.obstacles.map((o, i) => (
        <rect
          key={i}
          className={styles['wall']}
          x={o.x * CELL + 3}
          y={o.y * CELL + 3}
          width={CELL - 6}
          height={CELL - 6}
          rx={4}
        />
      ))}
      <g transform={at(room.caseCell)} className={styles['case']}>
        <rect x={5} y={5} width={CELL - 10} height={CELL - 10} rx={6} />
        <text x={CELL / 2} y={CELL / 2 + 5} textAnchor="middle">
          {'◆'}
        </text>
      </g>
      <g transform={at(room.receiver)} className={styles['receiver']}>
        <circle cx={CELL / 2} cy={CELL / 2} r={13} />
        <circle cx={CELL / 2} cy={CELL / 2} r={5} />
      </g>
      <g transform={at(room.emitter)} className={styles['emitter']}>
        <rect x={6} y={6} width={CELL - 12} height={CELL - 12} rx={5} />
        <text x={CELL / 2} y={CELL / 2 + 5} textAnchor="middle">
          {'▶'}
        </text>
      </g>
      {hideMirrorGlyph
        ? room.mirrors.map((m, i) => (
            <g key={i} transform={at(m)} className={styles['mirrorSpot']}>
              <rect x={6} y={6} width={CELL - 12} height={CELL - 12} rx={5} />
            </g>
          ))
        : null}
    </>
  );
}
